/**
 * Phase 45 Plan 45-08 — task acceptance criteria API client (D-15).
 *
 * Backend contract (45-04):
 *   GET    /api/tasks/:taskId/acceptance              → AcceptanceItem[]
 *   POST   /api/tasks/:taskId/acceptance              body: { text }
 *   PATCH  /api/tasks/:taskId/acceptance/:itemId      body: { text? | done? }
 *   DELETE /api/tasks/:taskId/acceptance/:itemId      → 204
 *
 * The PATCH endpoint multiplexes an explicit `done` assignment vs a text
 * update (`text`) via the body shape so we get one StateChange per field
 * touched. MKS-150: `{ done: X }` ASSIGNS X — it is idempotent, not a
 * toggle. `toggleAcceptanceItem` below sends the caller's DESIRED value
 * (see AcceptanceCriteriaList.tsx: `onToggle={(done) => ...}` passes
 * `e.target.checked`), so no caller behavior changes.
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

export interface AcceptanceItem {
  id: string;
  text: string;
  done: boolean;
}

export async function fetchAcceptance(taskId: number): Promise<AcceptanceItem[]> {
  const response = await apiGet<{ data: AcceptanceItem[] }>(`/tasks/${taskId}/acceptance`);
  return response.data;
}

export async function addAcceptanceItem(
  taskId: number,
  text: string,
): Promise<AcceptanceItem> {
  const response = await apiPost<{ data: AcceptanceItem }>(
    `/tasks/${taskId}/acceptance`,
    { text },
  );
  return response.data;
}

// MKS-150 — despite the name (kept for caller-compatibility), this sends the
// caller's DESIRED `done` value, which the backend now ASSIGNS (idempotent
// set semantics), not a presence-triggered flip.
export async function toggleAcceptanceItem(
  taskId: number,
  itemId: string,
  done: boolean,
): Promise<AcceptanceItem> {
  const response = await apiPatch<{ data: AcceptanceItem }>(
    `/tasks/${taskId}/acceptance/${itemId}`,
    { done },
  );
  return response.data;
}

export async function updateAcceptanceItemText(
  taskId: number,
  itemId: string,
  text: string,
): Promise<AcceptanceItem> {
  const response = await apiPatch<{ data: AcceptanceItem }>(
    `/tasks/${taskId}/acceptance/${itemId}`,
    { text },
  );
  return response.data;
}

export async function deleteAcceptanceItem(
  taskId: number,
  itemId: string,
): Promise<void> {
  await apiDelete<void>(`/tasks/${taskId}/acceptance/${itemId}`);
}
